import { Currency } from '@round/api';
import { asMoneyWithoutZeros } from 'helpers';
import styles from './TotalCostFooterCell.module.css';

type Props = {
    costs: (number | null)[];
    currency: Currency | undefined;
    // Server-side total as the decimal string `/posts/totals/` serves, used in place of reducing
    // `costs` when the API-totals flag is on. `null`, blank and unparseable all render as '-', the
    // way an empty cost column does; `undefined` means no total was passed and the rows are
    // reduced as before.
    total?: string | null;
};

// The totals endpoint serves monetary figures as decimal strings. Absent and unparseable both read
// as no cost rather than zero, so a missing total can never be mistaken for a free campaign.
const parseTotal = (value: string | null | undefined) => {
    if (typeof value !== 'string' || !value.trim()) {
        return null;
    }

    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
};

const TotalCostFooterCell = ({ costs, currency, total }: Props) => {
    const hasServerTotal = total !== undefined;
    const serverTotal = parseTotal(total);
    const totalCost = hasServerTotal
        ? serverTotal
        : costs.reduce((acc, cost) => (typeof cost === 'number' ? cost + (acc ?? 0) : acc), 0);
    const showCost = hasServerTotal ? serverTotal !== null : costs.some((cost) => cost !== null);

    return (
        <span className={styles.cell}>
            {showCost && typeof totalCost === 'number' ? asMoneyWithoutZeros(totalCost, currency) : '-'}
        </span>
    );
};

export default TotalCostFooterCell;
